import React from "react";

import { Breadcrumbs, Divider, Stack, useTheme, useViewport } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";

import { UIFlightSegment, UIOrder } from "@flights/types";
import useCreateUrl from "../../../hooks/useCreateUrl";
import { FilteredLegWithOpenedCheckIn } from "../../../utils/get-filtered-legs-with-opened-checkIn";

import ConfirmationController from "../ConfirmationController";
import { CheckInInfoCard } from "./CheckinInfoCard";
import TwoColumnLayout from "../TwoColumnLayout";
import { CheckinHeader } from "./CheckinHeader";
import { CheckinHowTo } from "./CheckinHowTo";
import { CheckinSegmentHeader } from "./CheckinSegmentHeader";
import { CheckinTimeInfoBanner } from "./CheckinTimeInfoBanner";
import { CheckinFAQs } from "./CheckinFAQs";
import { useTrackCheckinPage } from "../../../utils/experiments/post-booking/flights_web_pb_checkin_page_v2_m2";
import { useTrackCheckInMarketingCarrier } from "../../../utils/experiments/post-booking/flights_pb_universal_checkin_marketing_carrier";
import { useTrackCheckInLinks } from "../../../utils/experiments/post-booking/flights_pb_universal_checkin_links_cirium";
import { useTrackLoggedStatusSelfServeActions } from "../../../utils/experiments/post-booking/flights_web_pb_logged_status_self_serve_actions";
import { trackWithDefaultStage } from "@flights/et-universal";
import useIsPostBookingRoute from "../../../hooks/useIsPostBookingRoute";

interface WebCheckinInnerDesktopProps {
  order: UIOrder;
  segmentIndex: number;
  segment: UIFlightSegment;
  filteredLegsWithOpenedCheckIn: FilteredLegWithOpenedCheckIn[];
}

export const WebCheckinInnerDesktop: React.FC<WebCheckinInnerDesktopProps> = ({
  order,
  segment,
  filteredLegsWithOpenedCheckIn
}) => {
  const i18n = useI18n();
  const theme = useTheme();
  const { createUrl } = useCreateUrl();

  const { isCheckinPageExperimentVariant } = useTrackCheckinPage(order);

  useTrackCheckInMarketingCarrier();
  useTrackCheckInLinks(filteredLegsWithOpenedCheckIn.map((legWithOpenedCheckin) => legWithOpenedCheckin.leg));
  useTrackLoggedStatusSelfServeActions(order);

  const { isLarge } = useViewport();
  const isPostBooking = useIsPostBookingRoute();

  const isResponsiveFixes =
    isPostBooking && !isLarge && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_pb_www", 1);

  const content = (
    <div style={{ marginBottom: theme.units.spacing_8x }}>
      <Stack gap={8}>
        {isCheckinPageExperimentVariant && (
          <CheckinTimeInfoBanner
            isLessThan24Hours={filteredLegsWithOpenedCheckIn.some(
              ({ checkinInfoForLeg }) => checkinInfoForLeg.isOnlineCheckinOpen
            )}
          />
        )}
        {!isCheckinPageExperimentVariant && <CheckinHowTo order={order} />}
        {!isCheckinPageExperimentVariant && <Divider />}
        <Stack gap={4}>
          <CheckinSegmentHeader
            segment={segment}
            showSubHeader={filteredLegsWithOpenedCheckIn.length === 1}
            showMultiPnrAlert={filteredLegsWithOpenedCheckIn.length > 1}
          />
          {filteredLegsWithOpenedCheckIn.map(({ leg, checkinInfoForLeg }) => {
            const {
              legIdentifier: { segmentIndex, legIndex },
              isOnlineCheckinOpen
            } = checkinInfoForLeg;
            return (
              <CheckInInfoCard
                key={legIndex}
                leg={leg}
                segmentIndex={segmentIndex}
                segment={segment}
                legIndex={legIndex}
                openCheckin={isOnlineCheckinOpen}
                checkInInfo={checkinInfoForLeg}
                showHeader={filteredLegsWithOpenedCheckIn.length > 1}
                showVuelingAlert
                isUpdatedCheckInPageExperiment={isCheckinPageExperimentVariant}
              />
            );
          })}
          {!isLarge && isCheckinPageExperimentVariant && (
            <>
              <Divider />
              <CheckinFAQs />
            </>
          )}
        </Stack>
      </Stack>
    </div>
  );

  return (
    <ConfirmationController>
      {() => (
        <main>
          <Breadcrumbs
            back
            items={[
              {
                text: i18n.trans(t("flights_pb_checkin_navigation")),
                href: createUrl(`/booking/order-details/${order?.orderToken}`)
              }
            ]}
            attributes={{ style: { paddingBottom: theme.units.spacing_2x } }}
          />
          <Stack gap={6}>
            <CheckinHeader segment={segment} />
            {isResponsiveFixes ? (
              content
            ) : (
              <TwoColumnLayout sidebar={isCheckinPageExperimentVariant ? <CheckinFAQs /> : undefined}>
                {content}
              </TwoColumnLayout>
            )}
          </Stack>
        </main>
      )}
    </ConfirmationController>
  );
};
